import {useState} from 'react';
import {StyleSheet} from 'react-native';
import {Modal, Portal} from 'react-native-paper';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {Property, useFunds} from '../../hooks/useDomainData';
import {useLayoutInfo} from '../../hooks/useLayoutInfo';
import {useAppSettings} from '../AppSettings';
import {
  DefaultFormikStoreValidator,
  FormikStoreForm,
  FormikStoreType,
  FormikStoreTypes,
} from '../FormikStore';
import {useAlerts} from 'react-native-paper-alerts';

export interface PropertyEditorProps {
  dialogVisible: boolean;
  setDialogVisible: (visible: boolean) => void;
  property?: Property;
}

export function PropertyEditor(props: PropertyEditorProps): JSX.Element {
  const queryClient = useQueryClient();
  const Alert = useAlerts();

  const layoutInfo = useLayoutInfo();
  const appSettings = useAppSettings();
  const funds = useFunds();

  const [validating, setValidating] = useState(false);

  const collectionName = 'properties';
  const saveFirestoreObject = useMutation(
    (firestoreObject: Property) => {
      // remove properties that just are optional && undefined
      if (firestoreObject.propertyNotes === undefined) {
        delete firestoreObject.propertyNotes;
      }

      if (props.property === undefined) {
        // console.log(
        //   'adding: ' + JSON.stringify(firestoreObject, null, 2),
        // );

        // If there was no date set, delete the date property totally

        return (
          firestore()
            .collection(collectionName)
            // the types of .add and .set are incompatible, but we don't need the return value
            .add(firestoreObject) as Promise<unknown> as Promise<void>
        );
      }

      // console.log(
      //   'saving: ' + JSON.stringify(firestoreObject, null, 2),
      // );
      return firestore()
        .collection(collectionName)
        .doc(props.property?.ref.id)
        .set(firestoreObject, {merge: true});
    },
    {
      onSettled: () => {
        // console.log('saving succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
      onError: e => {
        Alert.alert('Error', 'There was an error while saving: ' + e, [
          {text: appSettings.t('OK')},
        ]);
      },
    },
  );

  const themedStyles = StyleSheet.create({
    formikStoreContent: {
      margin: 60,
      // flex: 1,
      borderColor: appSettings.currentTheme.colors.border,
      borderWidth: 2,
      backgroundColor: appSettings.currentTheme.colors.background,
      position: 'absolute',
      maxWidth: layoutInfo.screen.width - 120,
      maxHeight: layoutInfo.screen.height - 160,
      top: 0,
      right: 0,
      bottom: 0,
      left: 0,
    },
  });

  let property: {[key: string]: FormikStoreType} = {
    propertyId: Date.now(),
    propertyName: undefined,
    propertyUrl: undefined,
    propertyNotes: undefined,
    active: true,
    fundId: undefined,
  };

  if (props.property !== undefined) {
    property.propertyId = props.property.propertyId;
    property.propertyName = props.property.propertyName;
    property.propertyUrl = props.property.propertyUrl;
    property.propertyNotes = props.property.propertyNotes;
    property.active = props.property.active;
    property.fundId = props.property.fundId;
  }

  return (
    <Portal>
      <Modal
        contentContainerStyle={themedStyles.formikStoreContent}
        visible={props.dialogVisible}
        onDismiss={() => props.setDialogVisible(false)}>
        <FormikStoreForm
          objects={[
            {
              title: `${props.property ? 'Edit' : 'Create'} Property`,
              firestoreObject: property,
              fieldMetadata: [
                {
                  key: 'propertyId',
                  name: 'Property ID',
                  type: FormikStoreTypes.number,
                  required: true,
                  editable: false,
                  errorText: 'Property ID is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'propertyName',
                  name: 'Property Name',
                  type: FormikStoreTypes.text,
                  required: true,
                  errorText: 'Property Name is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'propertyUrl',
                  name: 'Property URL',
                  type: FormikStoreTypes.text,
                  required: false,
                  errorText: 'Property URL is a required field',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'fundId',
                  name: 'Fund',
                  type: FormikStoreTypes.select,
                  required: true,
                  errorText: 'Fund is a required field',
                  selectItems: {
                    documents: funds,
                    items: funds?.map(fund => fund.ref) ?? [],
                    labelExtractor: fund => fund?.fundCode,
                  },
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'active',
                  name: 'Active',
                  type: FormikStoreTypes.boolean,
                  required: false,
                  errorText: 'Must be true or false',
                  validator: DefaultFormikStoreValidator,
                },
                {
                  key: 'propertyNotes',
                  name: 'Property Notes',
                  type: FormikStoreTypes.textarea,
                  required: false,
                  errorText: 'Property Notes is an optional text field',
                  validator: DefaultFormikStoreValidator,
                },
              ],
            },
          ]}
          buttons={[
            {
              text: 'Cancel',
              validate: false,
              onPress: () => {
                props.setDialogVisible(false);
              },
            },
            {
              text: 'Save',
              validate: true,
              onPress: formObjects => {
                // console.log(
                //   'saving with ' +
                //     JSON.stringify(formObjects[0].firestoreObject, null, 2),
                // );
                setValidating(true);
                saveFirestoreObject.mutate(
                  formObjects[0].firestoreObject as unknown as Property,
                );
                props.setDialogVisible(false);
              },
            },
          ]}
          validateForm={validating}
        />
      </Modal>
    </Portal>
  );
}
